import type { OAuthApi } from '@backstage/frontend-plugin-api';
import type {
  Application,
  ServicePrincipal,
  User,
} from '@microsoft/microsoft-graph-types';

import type {
  IPenAppRoleAssignment,
  MsGraphSearchBatchResponse,
  MsGraphValueResponse,
  MsIamApi,
  MsOwnedObject,
  SearchResult,
} from '../types';

import { SearchResultType } from '../types';

type MsIamApiClientOptions = {
  microsoftAuthApi: OAuthApi;
};

export class MsIamApiClient implements MsIamApi {
  constructor(private readonly options: MsIamApiClientOptions) {}

  async createServicePrincipalsAppRoleAssignedTo(
    principalId: string,
    resourceId: string,
    appRoleId: string,
  ): Promise<IPenAppRoleAssignment> {
    const url = this.getMsGraphUrl(
      `servicePrincipals/${resourceId}/appRoleAssignedTo`,
    );

    const response = await this.fetchMsGraph(url, {
      body: JSON.stringify({
        appRoleId,
        principalId,
        resourceId,
      }),
      headers: {
        'content-type': 'application/json',
      },
      method: 'POST',
    });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(response);
      throw new Error(`Failed to create app role assignment: ${errorMessage}`);
    }

    return (await response.json()) as IPenAppRoleAssignment;
  }

  async deleteServicePrincipalsAppRoleAssignedTo(
    resourceId: string,
    appRoleId: string,
  ): Promise<Response> {
    return await this.fetchMsGraph(
      this.getMsGraphUrl(
        `servicePrincipals/${resourceId}/appRoleAssignedTo/${appRoleId}`,
      ),
      {
        method: 'DELETE',
      },
    );
  }

  async getApplication(appId: string): Promise<Application> {
    const url = this.getMsGraphUrl(`applications/${appId}`);

    const response = await this.fetchMsGraph(url, {
      headers: {
        ConsistencyLevel: 'session',
      },
    });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(response);
      throw new Error(`Failed to get application: ${errorMessage}`);
    }

    return (await response.json()) as Application;
  }

  async getAppRoleAssignmentsForApplication(
    appId: string,
  ): Promise<MsGraphValueResponse<IPenAppRoleAssignment>> {
    const { id: spnId } = await this.getServicePrincipalForApp(appId);

    let link: string | undefined = this.getMsGraphUrl(`servicePrincipals/${spnId}/appRoleAssignedTo`).href;
    const result: MsGraphValueResponse<IPenAppRoleAssignment> = { value: [] };
    while (link) {
      const res = await this.fetchMsGraph(link, {
        headers: {
          'content-type': 'application/json',
        },
        method: 'GET',
      });
      if (res.status >= 400) {
        throw new Error(
          `App roles not found for application with id: ${appId}`,
        );
      }
      const val = await res.json();
      result.value.push(...val.value);

      link = val['@odata.nextLink'];
    }
    return result;
  }

  async getMyOwnedObjects(): Promise<MsGraphValueResponse<MsOwnedObject>> {
    let link: string | undefined = this.getMsGraphUrl('me/ownedObjects?$top=100').href;
    const result: MsGraphValueResponse<MsOwnedObject> = { value: [] };
    while (link) {
      const res = await (
        await this.fetchMsGraph(link, {
          headers: {
            'content-type': 'application/json',
          },
          method: 'GET',
        })
      ).json();
      result.value.push(...res.value);
      link = res['@odata.nextLink'];
    }

    return {
      value: result.value.map<MsOwnedObject>(obj => {
        const type = obj['@odata.type'];
        if (!type) {
          throw new Error('Owned Microsoft Graph object is missing its type');
        }
        return {
          appId: obj.appId,
          displayName: obj.displayName,
          id: obj.id,
          type,
        };
      }),
    };
  }

  async getServicePrincipalForApp(appId: string): Promise<ServicePrincipal> {
    const url = this.getMsGraphUrl(`servicePrincipals(appId='${appId}')`);

    const response = await this.fetchMsGraph(url);

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(response);
      throw new Error(
        `Failed to get service principal for app: ${errorMessage}`,
      );
    }

    return (await response.json()) as ServicePrincipal;
  }

  async listUsers(query?: string): Promise<MsGraphValueResponse<User>> {
    const url = this.getMsGraphUrl('users');

    if (query) {
      url.searchParams.append(
        '$search',
        `displayName:${query} OR mail:${query}`,
      );
    }

    url.searchParams.append('$top', '10');

    const response = await this.fetchMsGraph(url, {
      headers: {
        ConsistencyLevel: 'eventual',
      },
    });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(response);
      throw new Error(`Failed to list users: ${errorMessage}`);
    }

    return (await response.json()) as MsGraphValueResponse<User>;
  }

  async searchServicePrincipals(query?: string): Promise<SearchResult[]> {
    const groupSearch = query ? `$search="displayName:${query}"&` : '';

    const body = {
      requests: [
        {
          headers: {
            ConsistencyLevel: 'eventual',
          },
          id: '1',
          method: 'GET',
          url: `/servicePrincipals?${groupSearch}$top=10`,
        },
      ],
    };

    return this.searchMsGraph(body, r => {
      if (!r.body.value) {
        return [];
      }

      if (r.id === '1') {
        return r.body.value.map(value => ({
          type: SearchResultType.ServicePrincipal,
          value,
        }));
      }

      return [];
    });
  }

  async searchUsersAndGroups(query?: string): Promise<SearchResult[]> {
    const userSearch = query
      ? `$search="displayName:${query}" OR "mail:${query}" OR "userPrincipalName:${query}" &`
      : '';

    const groupSearch = query ? `$search="displayName:${query}"&` : '';

    const body = {
      requests: [
        {
          headers: {
            ConsistencyLevel: 'eventual',
          },
          id: '1',
          method: 'GET',
          url: `/users?${userSearch}$top=10`,
        },
        {
          headers: {
            ConsistencyLevel: 'eventual',
          },
          id: '2',
          method: 'GET',
          url: `/groups?${groupSearch}$top=10`,
        },
      ],
    };

    return this.searchMsGraph(body, r => {
      if (!r.body.value) {
        return [];
      }

      switch (r.id) {
        case '1':
          return r.body.value.map(value => ({
            type: SearchResultType.User,
            value,
          })) as SearchResult[];
        case '2':
          return r.body.value.map(value => ({
            type: SearchResultType.Group,
            value,
          })) as SearchResult[];
        default:
          return [];
      }
    });
  }

  async updateApplication(appObjectId: string, body: unknown): Promise<void> {
    const url = this.getMsGraphUrl(`applications/${appObjectId}`);

    const response = await this.fetchMsGraph(url, {
      body: JSON.stringify(body),
      headers: {
        'content-type': 'application/json',
      },
      method: 'PATCH',
    });

    if (response.ok) return;

    const errorMessage = await this.getErrorMessage(response);

    throw new Error(`Failed to update application: ${errorMessage}`);
  }

  private async fetchMsGraph(
    input: string | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const token = await this.options.microsoftAuthApi.getAccessToken('User.Read');
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${token}`);

    return await fetch(input, {
      ...init,
      headers,
    });
  }

  private async getErrorMessage(response: Response): Promise<string> {
    const body = await response.text();

    if (!body) {
      return `${response.status} ${response.statusText}`.trim();
    }

    try {
      const json = JSON.parse(body) as { error?: { message?: string } };

      return json.error?.message ?? body;
    } catch {
      return body;
    }
  }

  private getMsGraphUrl(path: string): URL {
    return new URL(path, 'https://graph.microsoft.com/v1.0/');
  }

  private async searchMsGraph(
    body: unknown,
    mapResponse: (
      response: MsGraphSearchBatchResponse['responses'][number],
    ) => SearchResult[],
  ): Promise<SearchResult[]> {
    const response = await this.fetchMsGraph(this.getMsGraphUrl('$batch'), {
      body: JSON.stringify(body),
      headers: {
        'ConsistencyLevel': 'eventual',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    const json = (await response.json()) as MsGraphSearchBatchResponse;

    return json.responses.flatMap(mapResponse);
  }
}
